/* Shared, user-triggered motion. Never intercept payment submission. */
(() => {
  if (window.AuraInteractions) return;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const enabled = () => !reduced.matches && document.documentElement.dataset.auraMotion === 'true' && !window.Shopify?.designMode;
  const active = new Set();
  const animate = (element, frames, options) => {
    if (!enabled() || !element?.animate) return null;
    const animation = element.animate(frames, options);
    active.add(animation);
    animation.finished.catch(() => {}).finally(() => active.delete(animation));
    return animation;
  };
  reduced.addEventListener('change', () => { if (reduced.matches) { active.forEach(animation => animation.cancel()); active.clear(); } });
  const cartIcon = '<svg viewBox="0 0 40 40" fill="none" aria-hidden="true"><path d="M4 7h5l5 21h18l4-15H11M17 7v9m-4-4 4 4 4-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="17" cy="33" r="2" fill="currentColor"/><circle cx="30" cy="33" r="2" fill="currentColor"/></svg>';
  const timers = new WeakMap();
  let announcement;
  const cartState = (button, state) => {
    if (!button) return;
    clearTimeout(timers.get(button));
    button.classList.add('aura-cart-motion');
    button.dataset.auraCartState = state;
    let scene = button.querySelector('.aura-cart-motion__scene');
    if (!scene) {
      scene = document.createElement('span'); scene.className = 'aura-cart-motion__scene'; scene.setAttribute('aria-hidden', 'true');
      scene.innerHTML = cartIcon + '<strong>Added to your aura</strong>'; button.append(scene);
    }
    if (state === 'success') {
      if (!announcement) { announcement=document.createElement('p');announcement.className='visually-hidden';announcement.setAttribute('role','status');document.body.append(announcement); }
      announcement.textContent='Added to your cart.';
      animate(scene.querySelector('svg'), [{ transform:'translateX(-50px) rotate(-12deg)',opacity:0 },{transform:'translateX(0) rotate(0deg)',opacity:1,offset:.55},{transform:'translateX(0)',opacity:1}], {duration:650,easing:'cubic-bezier(.22,.61,.36,1)'});
      timers.set(button, setTimeout(() => { button.dataset.auraCartState = 'idle'; }, 1300));
    } else if (announcement) {
      announcement.textContent='';
    }
  };
  window.AuraInteractions = { enabled, animate, cartState };

  // Product feature wheel is server-rendered; its links remain useful without JS.
  const wheelCleanups = new Map();
  function initWheel(root) {
    if (root.dataset.ready) return;
    const pool = [...root.querySelectorAll('.aura-featured-wheel__item')];
    root.querySelectorAll('template[data-aura-wheel-candidate]').forEach(template => {
      const card = template.content.querySelector('.aura-featured-wheel__item');
      if (card) pool.push(card.cloneNode(true));
      template.remove();
    });
    const limit = Math.max(2, Number(root.dataset.auraSelectionLimit) || pool.length);
    const storageKey = 'aura-wheel-selection:' + (root.closest('.shopify-section')?.id || 'home');
    let previous = [];
    try { previous = JSON.parse(sessionStorage.getItem(storageKey) || '[]'); } catch (_) {}
    if (!Array.isArray(previous)) previous = [];
    for (let i = pool.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }
    // Prefer products absent from the last visit, then fill from the remaining pool.
    const fresh = pool.filter(item => !previous.includes(item.getAttribute('href')));
    const seen = pool.filter(item => previous.includes(item.getAttribute('href')));
    const items = [...fresh, ...seen].slice(0, limit);
    if (items.length > 1 && items[0].getAttribute('href') === previous[0]) items.push(items.shift());
    pool.forEach(item => { if (!items.includes(item)) item.remove(); });
    const wheelStage = root.querySelector('.aura-featured-wheel__stage');
    items.forEach(item => wheelStage.append(item));
    try { sessionStorage.setItem(storageKey, JSON.stringify(items.map(item => item.getAttribute('href')))); } catch (_) {}
    if (items.length < 2) return;
    root.dataset.ready = 'true';
    const stage = root.querySelector('.aura-featured-wheel__stage');
    const detail = root.querySelector('.aura-featured-wheel__detail');
    let selected=0, phase=0, target=0, start=null, suppress=0;
    const state={phase:0};
    let stageWidth=stage.clientWidth, dragFrame=0, motionFrame=0;
    const narrow=matchMedia('(max-width:749px)');
    const stop=()=>{cancelAnimationFrame(motionFrame);motionFrame=0;cancelAnimationFrame(dragFrame);dragFrame=0;};
    const layers=items.map(item=>{item.style.willChange='transform, opacity';item.style.backfaceVisibility='hidden';item.style.transition='none';return {item,z:0};});
    const paint=()=>{
      const radius=stageWidth*(narrow.matches?.36:.40);
      const poses=layers.map((layer,i)=>{const angle=(i-state.phase)*Math.PI*2/items.length;const depth=(Math.cos(angle)+1)/2;return {layer,depth,x:Math.sin(angle)*radius,y:(1-depth)*-32,scale:.58+depth*.42,rotation:Math.sin(angle)*-9};});
      poses.slice().sort((a,b)=>a.depth-b.depth).forEach((p,i)=>{if(p.layer.z!==i+1){p.layer.z=i+1;p.layer.item.style.zIndex=String(i+1);}});
      poses.forEach(p=>{p.layer.item.style.transform=`translate(-50%,-50%) translate3d(${p.x}px,${p.y}px,0) scale(${p.scale}) rotate(${p.rotation}deg)`;p.layer.item.style.opacity=String(.12+.88*p.depth*p.depth*p.depth);});
    };
    const go=(next,motion=true)=>{
      stop();target=next;phase=next;selected=((next%items.length)+items.length)%items.length;
      items.forEach((item,i)=>{const current=i===selected;item.tabIndex=current?0:-1;item.setAttribute('aria-current',String(current));item.setAttribute('aria-hidden',String(!current));});
      const item=items[selected];
      detail.querySelector('a').href=item.href;detail.querySelector('a').textContent=item.dataset.title;detail.querySelector('p').textContent=item.dataset.price;
      if(!motion||!enabled()){state.phase=target;paint();return;}
      const from=state.phase,distance=Math.abs(target-from),duration=narrow.matches?Math.min(300,180+distance*70):420;
      let began;
      const step=now=>{if(began===undefined)began=now;const t=Math.min(1,(now-began)/duration);state.phase=from+(target-from)*(1-Math.pow(1-t,3));paint();if(t<1)motionFrame=requestAnimationFrame(step);else{motionFrame=0;state.phase=target;}};
      motionFrame=requestAnimationFrame(step);
    };
    root.querySelector('[data-wheel-prev]').addEventListener('click',()=>go(phase-1));
    root.querySelector('[data-wheel-next]').addEventListener('click',()=>go(phase+1));
    stage.addEventListener('keydown',event=>{if(['ArrowLeft','ArrowRight','Home','End'].includes(event.key)){event.preventDefault();go(event.key==='Home'?0:event.key==='End'?items.length-1:phase+(event.key==='ArrowRight'?1:-1));}});
    items.forEach((item,i)=>item.addEventListener('click',event=>{if(performance.now()<suppress){event.preventDefault();return;}if(i!==selected){event.preventDefault();let delta=i-selected;if(delta>items.length/2)delta-=items.length;if(delta<-items.length/2)delta+=items.length;go(phase+delta);}}));
    stage.addEventListener('pointerdown',event=>{if(event.button!==0||start)return;stop();start={x:event.clientX,y:event.clientY,phase:state.phase,id:event.pointerId,dragging:false};});
    const move=event=>{
      if(!start||event.pointerId!==start.id)return;
      const dx=event.clientX-start.x,dy=event.clientY-start.y;
      if(!start.dragging){if(Math.abs(dy)>10&&Math.abs(dy)>Math.abs(dx)){start=null;go(phase);return;}if(Math.abs(dx)<8)return;start.dragging=true;stage.setPointerCapture?.(event.pointerId);}
      state.phase=start.phase-dx/Math.max(90,stageWidth*.32);
      if(!dragFrame)dragFrame=requestAnimationFrame(()=>{dragFrame=0;paint();});
    };
    stage.addEventListener('pointermove',move,{passive:true});
    const up=event=>{
      if(!start||event.pointerId!==start.id)return;
      const gesture=start;start=null;if(!gesture.dragging){go(phase);return;}
      const dx=event.clientX-gesture.x;state.phase=gesture.phase-dx/Math.max(90,stageWidth*.32);
      stop();paint();suppress=performance.now()+450;
      let next=Math.round(state.phase);if(Math.abs(dx)>35&&next===Math.round(gesture.phase))next+=dx<0?1:-1;
      go(next);
    };
    window.addEventListener('pointerup',up);
    stage.addEventListener('dragstart',event=>event.preventDefault());
    const cancel=event=>{if(!start||(event&&event.pointerId!==start.id))return;const next=start.dragging?Math.round(state.phase):phase;start=null;go(next);};
    window.addEventListener('pointercancel',cancel);
    const resize=()=>{stageWidth=stage.clientWidth;paint();};window.addEventListener('resize',resize,{passive:true});
    const reduce=()=>go(phase,false);reduced.addEventListener('change',reduce);
    const cleanup=()=>{stop();stage.removeEventListener('pointermove',move);window.removeEventListener('pointerup',up);window.removeEventListener('pointercancel',cancel);window.removeEventListener('resize',resize);reduced.removeEventListener('change',reduce);wheelCleanups.delete(root);};
    wheelCleanups.set(root,cleanup);
    root.closest('.shopify-section')?.addEventListener('shopify:section:unload',cleanup,{once:true});
    go(0,false);
    window.AuraGsapReady?.().catch(()=>{});
  }
  function init() {
    document.querySelectorAll('[data-aura-featured-wheel]').forEach(initWheel);
    document.querySelectorAll('.aura-orbit--directory').forEach(root => {
      if(root.querySelector('.aura-constellation'))return;
      const ns='http://www.w3.org/2000/svg',svg=document.createElementNS(ns,'svg');
      svg.setAttribute('viewBox','0 0 1200 600');svg.setAttribute('preserveAspectRatio','xMidYMid slice');svg.setAttribute('aria-hidden','true');svg.classList.add('aura-constellation');
      const points=[[45,120],[140,195],[215,90],[280,220],[110,365],[350,490],[870,80],[1000,170],[1120,110],[1030,365],[1170,465]];
      [[0,1],[1,2],[1,3],[1,4],[4,5],[6,7],[7,8],[7,9],[9,10]].forEach(([a,b])=>{const line=document.createElementNS(ns,'line');line.setAttribute('x1',points[a][0]);line.setAttribute('y1',points[a][1]);line.setAttribute('x2',points[b][0]);line.setAttribute('y2',points[b][1]);line.setAttribute('stroke','#d4af37');line.setAttribute('stroke-opacity','.3');svg.append(line);});
      points.forEach(([x,y])=>{const point=document.createElementNS(ns,'circle');point.setAttribute('cx',x);point.setAttribute('cy',y);point.setAttribute('r','2.5');point.setAttribute('fill','#edcd66');svg.append(point);});
      root.prepend(svg);
      root.addEventListener('pointerup',()=>animate(svg,[{opacity:.3},{opacity:.7,offset:.4},{opacity:.5}],{duration:850}));
    });
    document.querySelectorAll('[data-aura-welcome]').forEach(dialog=> {
      if(dialog.querySelector('.aura-portal-rings'))return;
      const rings=document.createElement('div'); rings.className='aura-portal-rings'; rings.setAttribute('aria-hidden','true'); dialog.prepend(rings);
      dialog.querySelector('.aura-welcome__enter')?.addEventListener('click',()=>animate(rings,[{transform:'translate(-50%,-50%) scale(.85) rotate(0deg)',opacity:.6},{transform:'translate(-50%,-50%) scale(1.8) rotate(55deg)',opacity:0}],{duration:300,easing:'ease-out'}));
    });
  }
  document.addEventListener('shopify:section:load',init);
  document.addEventListener('aura:collections-ready',init);
  window.addEventListener('pageshow',event=>document.querySelectorAll('[data-aura-cart-state]').forEach(button=>{
    if(event.persisted&&['loading','success'].includes(button.dataset.auraCartState)){
      button.classList.remove('loading');button.querySelector('.loading__spinner')?.classList.add('hidden');
      if(!button.disabled)button.removeAttribute('aria-disabled');
    }
    cartState(button,'idle');
  }));
  const boot=()=>{
    init();
    const grid=document.getElementById('ProductGridContainer');
    if(grid)new MutationObserver(()=>{for(const [root,cleanup] of wheelCleanups)if(!root.isConnected)cleanup();grid.querySelectorAll('[data-aura-featured-wheel]').forEach(initWheel);}).observe(grid,{childList:true,subtree:true});
  };
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',boot,{once:true});else boot();
})();

/* The artwork pins behind the flowing product section; no extra scroll spacer. */
(() => {
  if(window.AuraCollectionScenes)return;
  window.AuraCollectionScenes=true;
  const scenes=new Map();
  let library;
  function scrollLibrary(){
    if(library)return library;
    library=Promise.resolve(window.AuraGsapReady?.()).then(()=>{
      if(!window.gsap)throw Error('GSAP unavailable');
      if(window.ScrollTrigger)return;
      return new Promise((resolve,reject)=>{
        const script=document.createElement('script');
        const timer=setTimeout(()=>reject(Error('ScrollTrigger timeout')),5000);
        script.src='https://cdnjs.cloudflare.com/ajax/libs/gsap/3.13.0/ScrollTrigger.min.js';
        script.async=true;
        script.onload=()=>{clearTimeout(timer);resolve();};
        script.onerror=()=>{clearTimeout(timer);reject(Error('ScrollTrigger unavailable'));};
        document.head.append(script);
      });
    }).then(()=>{if(!window.ScrollTrigger)throw Error('ScrollTrigger unavailable');window.gsap.registerPlugin(window.ScrollTrigger);});
    return library;
  }
  function init(){
    if(document.documentElement.dataset.auraMotion!=='true'||window.Shopify?.designMode)return;
    document.querySelectorAll('.aura-collection-portal[data-aura-banner]').forEach(hero=>{
      if(scenes.has(hero))return;
      const light=hero.querySelector('.aura-banner-light'),dust=hero.querySelector('.aura-banner-dust');
      if(!light||!dust)return;
      const motion=matchMedia('(prefers-reduced-motion: reduce)');
      let mm,disposed=false;
      const stop=()=>{mm?.revert();mm=null;hero.dataset.auraScene='static';};
      const start=()=>{
        stop();
        if(motion.matches||disposed)return;
        scrollLibrary().then(()=>{
          if(disposed||motion.matches||!hero.isConnected||mm)return;
          const gsap=window.gsap;
          mm=gsap.matchMedia();
          mm.add({desktop:'(min-width:750px)',mobile:'(max-width:749px)'},context=>{
            const mobile=context.conditions.mobile;
            const art=hero.querySelector('img'),surface=art?.parentElement,section=hero.closest('.shopify-section');
            if(!art||!surface||!section)return;
            const foreground=[];
            for(let next=section.nextElementSibling;next;next=next.nextElementSibling){next.classList.add('aura-collection-foreground');foreground.push(next);}
            section.classList.add('aura-collection-backdrop');
            hero.classList.add('aura-collection-pinning');
            let frame=0,resizeNeeded=true,height=0;
            const draw=()=>{
              frame=0;
              const box=hero.getBoundingClientRect(),ratio=Number(art.getAttribute('height'))/Number(art.getAttribute('width'));
              if(!ratio||!box.width)return;
              if(resizeNeeded){
                resizeNeeded=false;height=box.width*ratio;
                hero.style.setProperty('--aura-pinned-height',height+'px');
                hero.style.setProperty('--aura-pinned-width',box.width+'px');
                hero.style.setProperty('--aura-pinned-left',box.left+'px');
              }
              surface.classList.toggle('is-pinned',box.top<=0);
              surface.style.visibility=box.top+height<=0?'hidden':'visible';
            };
            const queue=()=>{if(!frame)frame=requestAnimationFrame(draw);};
            addEventListener('scroll',queue,{passive:true});
            const measure=()=>{resizeNeeded=true;queue();};
            addEventListener('resize',measure,{passive:true});
            const resize=new ResizeObserver(measure);resize.observe(hero);
            draw();
            gsap.fromTo(art,{scale:1},{scale:mobile?1.025:1.045,ease:'none',scrollTrigger:{trigger:hero,start:'top top',end:'bottom top',scrub:.18,invalidateOnRefresh:true}});
            const timeline=gsap.timeline({scrollTrigger:{trigger:hero,start:'top 80%',end:'bottom top',scrub:.35,invalidateOnRefresh:true},defaults:{ease:'none'}});
            timeline.fromTo(light,{xPercent:mobile?-6:-14,yPercent:3,opacity:.12},{xPercent:mobile?8:20,yPercent:-5,opacity:.55,duration:1},0);
            if(!mobile)timeline.fromTo(dust,{yPercent:6,xPercent:-2,opacity:.12},{yPercent:-12,xPercent:4,opacity:.32,duration:1},0);
            return ()=>{
              cancelAnimationFrame(frame);resize.disconnect();
              removeEventListener('scroll',queue);removeEventListener('resize',measure);
              surface.classList.remove('is-pinned');surface.style.removeProperty('visibility');
              hero.classList.remove('aura-collection-pinning');section.classList.remove('aura-collection-backdrop');
              ['--aura-pinned-height','--aura-pinned-width','--aura-pinned-left'].forEach(name=>hero.style.removeProperty(name));
              foreground.forEach(element=>element.classList.remove('aura-collection-foreground'));
            };
          });
          hero.dataset.auraScene='scroll';
        }).catch(()=>{hero.dataset.auraScene='static';});
      };
      motion.addEventListener('change',start);
      const image=hero.querySelector('img');
      const refresh=()=>window.ScrollTrigger?.refresh();
      image?.addEventListener('load',refresh);
      scenes.set(hero,()=>{disposed=true;stop();motion.removeEventListener('change',start);image?.removeEventListener('load',refresh);scenes.delete(hero);});
      start();
    });
  }
  document.addEventListener('shopify:section:unload',event=>{for(const [hero,cleanup] of scenes)if(event.target.contains(hero))cleanup();});
  document.addEventListener('shopify:section:load',init);
  addEventListener('pageshow',()=>window.ScrollTrigger?.refresh());
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',init,{once:true});else init();
})();

/* Product motion observes native gallery changes; never submits or replaces forms. */
(() => {
  if(window.AuraProductScenes)return;
  window.AuraProductScenes=true;
  const roots=new Map(),reduce=matchMedia('(prefers-reduced-motion: reduce)');
  function init(){
    document.querySelectorAll('product-info[id^="MainProduct-"]').forEach(root=>{
      if(roots.has(root))return;
      const allowed=()=>!reduce.matches&&window.AuraInteractions?.enabled();
      const running=new Map();let disposed=false,frame=0,lastImage='',gsap;
      const motion=(element,from,to,duration=.55,delay=0)=>{
        if(!element||!allowed())return;
        running.get(element)?.kill?.();running.get(element)?.cancel?.();
        if(gsap){const tween=gsap.fromTo(element,from,{...to,duration,delay,ease:'power2.out',clearProps:'opacity,transform',onComplete:()=>running.delete(element)});running.set(element,tween);}
        else {const a=element.animate([{opacity:from.opacity??1,transform:'translateY('+(from.y||0)+'px)'},{opacity:1,transform:'none'}],{duration:duration*1000,delay:delay*1000,easing:'ease-out'});running.set(element,a);a.finished.catch(()=>{}).finally(()=>{if(running.get(element)===a)running.delete(element);});}
      };
      const stop=()=>{running.forEach(a=>{a.kill?.();a.cancel?.();});running.forEach((_,e)=>{e.style.removeProperty('opacity');e.style.removeProperty('transform');});running.clear();};
      const gallery=root.querySelector('media-gallery'),media=root.querySelector('.product__media-wrapper');
      const imageChanged=()=>{
        frame=0;if(disposed||!gallery)return;
        const image=gallery.querySelector('.product__media-item.is-active .product__media img')||[...gallery.querySelectorAll('.product__media img')].find(i=>i.getClientRects().length);
        if(!image)return;
        const key=(image.closest('[data-media-id]')?.dataset.mediaId||'')+'|'+image.getAttribute('src');
        if(key===lastImage)return;const firstImage=!lastImage;lastImage=key;if(firstImage)return;
        motion(image,{opacity:.7,y:12},{opacity:1,y:0},.5);
      };
      const queue=()=>{if(!frame)frame=requestAnimationFrame(imageChanged);};
      const observer=new MutationObserver(queue);
      if(gallery)observer.observe(gallery,{childList:true,subtree:true,attributes:true,attributeFilter:['src','srcset','class']});
      gallery?.addEventListener('load',queue,true);
      const reveal=new IntersectionObserver(entries=>entries.forEach(entry=>{
        if(!entry.isIntersecting)return;reveal.unobserve(entry.target);
        motion(entry.target,{opacity:.7,y:20},{opacity:1,y:0},.65,Number(entry.target.dataset.auraSceneOrder||0)*.07);
      }),{threshold:.08});
      document.querySelectorAll('.product__description,.ar-promise__eyebrow,.ar-promise h2,.ar-promise__card').forEach((element,index)=>{element.dataset.auraSceneOrder=index%3;reveal.observe(element);});
      let scrollFrame=0;
      const paint=()=>{scrollFrame=0;const box=root.getBoundingClientRect();root.style.setProperty('--aura-product-progress',allowed()?String(Math.max(0,Math.min(1,-box.top/Math.max(1,box.height-innerHeight)))):'0');};
      const scroll=()=>{if(!scrollFrame)scrollFrame=requestAnimationFrame(paint);};
      const pointer=event=>{if(!allowed()||event.pointerType!=='mouse'||!media)return;const b=media.getBoundingClientRect();media.style.setProperty('--aura-light-x',Math.round((event.clientX-b.left)/b.width*100)+'%');media.style.setProperty('--aura-light-y',Math.round((event.clientY-b.top)/b.height*100)+'%');};
      const leave=()=>{media?.style.removeProperty('--aura-light-x');media?.style.removeProperty('--aura-light-y');};
      media?.addEventListener('pointermove',pointer);media?.addEventListener('pointerleave',leave);
      addEventListener('scroll',scroll,{passive:true});addEventListener('resize',scroll,{passive:true});
      const preference=()=>{if(reduce.matches){stop();leave();}paint();};reduce.addEventListener('change',preference);
      roots.set(root,()=>{disposed=true;stop();observer.disconnect();reveal.disconnect();cancelAnimationFrame(frame);cancelAnimationFrame(scrollFrame);gallery?.removeEventListener('load',queue,true);media?.removeEventListener('pointermove',pointer);media?.removeEventListener('pointerleave',leave);removeEventListener('scroll',scroll);removeEventListener('resize',scroll);reduce.removeEventListener('change',preference);roots.delete(root);});
      Promise.resolve(allowed()?window.AuraGsapReady?.():null).then(()=>{if(disposed)return;gsap=window.gsap;queue();}).catch(queue);
      paint();
    });
  }
  document.addEventListener('shopify:section:load',init);
  document.addEventListener('shopify:section:unload',event=>{for(const [root,cleanup]of roots)if(event.target.contains(root))cleanup();});
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',init,{once:true});else init();
})();

/* Product-page recommendations, variant cards and success-only cart motion. */
(() => {
  if (window.AuraShopping) return;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const motion = () => !reduced.matches && window.AuraInteractions?.enabled();
  async function cartAdded(button) {
    const target = document.querySelector('#cart-icon-bubble');
    const product = button.closest('product-info');
    const source = button.closest('.card-wrapper')?.querySelector('.card__media img') || product?.querySelector('media-gallery .is-active img') || product?.querySelector('media-gallery img');
    if (!motion() || !target || !source) return;
    document.documentElement.classList.add('ar-cart-in-flight');
    let image;
    try {
      await new Promise(resolve => setTimeout(resolve, 180));
      if (!button.isConnected || !motion()) return;
      const art = source.getBoundingClientRect(), control = button.getBoundingClientRect(), to = target.getBoundingClientRect();
      const from = art.bottom > 0 && art.top < innerHeight && art.width > 0 ? art : control;
      const size = Math.min(110, Math.max(72, from.width * .35));
      const x = Math.max(12, Math.min(innerWidth-size-12,from.x+from.width/2-size/2));
      const y = Math.max(80, Math.min(innerHeight-size-30,from.y+Math.min(from.height/2,innerHeight/2)-size/2));
      const endX = Math.max(20,Math.min(innerWidth-20,to.x+to.width/2)),endY = Math.max(28,Math.min(innerHeight-28,to.y+to.height/2));
      image = document.createElement('img');image.src=source.currentSrc||source.src;image.alt='';image.className='aura-cart-flight';image.setAttribute('aria-hidden','true');
      Object.assign(image.style,{left:x+'px',top:y+'px',width:size+'px',height:size+'px',objectFit:'contain',border:'1px solid #cdb77b',background:'#eeeade',boxShadow:'0 0 24px #cdb77b66',borderRadius:'12px'});document.body.append(image);
      const dx=endX-x-size/2,dy=endY-y-size/2;
      const animation=window.AuraInteractions.animate(image,[{transform:'translate(0,0) scale(1)',opacity:1},{transform:`translate(${dx*.4}px,${Math.min(dy*.5,-100)}px) scale(.85) rotate(-10deg)`,opacity:1,offset:.45},{transform:`translate(${dx}px,${dy}px) scale(.12)`,opacity:.25}],{duration:900,easing:'cubic-bezier(.3,0,.5,1)'});
      await animation?.finished.catch(()=>{});
      image.remove();
      const bounce=window.AuraInteractions.animate(target,[{transform:'scale(1)'},{transform:'scale(1.25)'},{transform:'scale(1)'}],{duration:260});
      await bounce?.finished.catch(()=>{});
    } finally {image?.remove();document.documentElement.classList.remove('ar-cart-in-flight');}
  }
  window.AuraShopping = { cartAdded };

  const wheels = new WeakSet();
  function initRecommendations(root) {
    const list = root.querySelector('[data-aura-recommendation-list]');
    if (!list || wheels.has(list)) return;
    // Vary eligible Shopify recommendations once per section load.
    const pool=[...list.children];
    for(let i=pool.length-1;i>0;i--){const j=Math.floor(Math.random()*(i+1));[pool[i],pool[j]]=[pool[j],pool[i]];}
    const productLink=card=>card?.querySelector('.card__heading a')?.getAttribute('href')?.split('?')[0]||'';
    try {
      const key='aura-recommendation-first-'+root.dataset.productId;
      if(pool.length>1&&sessionStorage.getItem(key)===productLink(pool[0]))[pool[0],pool[1]]=[pool[1],pool[0]];
      sessionStorage.setItem(key,productLink(pool[0]));
    } catch (_) { /* Storage can be unavailable in private browsing. */ }
    pool.slice(5).forEach(card=>card.remove());
    pool.slice(0,5).forEach(card=>list.append(card));

    const cards = [...list.children];
    if (!cards.length) return;
    wheels.add(list);
    list.querySelectorAll('.card__media img').forEach(image=>image.sizes='(min-width: 750px) 45vw, 90vw');
    if (cards.length < 2) return;
    root.classList.add('aura-recs-ready');
    const controls = root.querySelector('[data-aura-recommendation-controls]'); controls.hidden = false;
    const mobileCards=matchMedia('(max-width:749px)');
    let index=0, start=null;
    let measureFrame=0, measuredHeight=0, measuredMax=0, swipeStep=1;
    const measureCards=()=>{
      measureFrame=0;
      if(!list.isConnected)return;
      swipeStep=Math.max(1,cards[0].offsetWidth*.65);
      const maxHeight=Math.ceil(Math.max(...cards.map(card=>card.offsetHeight)));
      const height=mobileCards.matches?maxHeight:Math.ceil(cards[index].offsetHeight);
      if(maxHeight!==measuredMax){measuredMax=maxHeight;list.style.setProperty('--aura-rec-height',maxHeight+'px');}
      if(height>0&&height!==measuredHeight){
        measuredHeight=height;
        list.style.height=height+'px';

      }
    };
    const queueMeasure=()=>{if(!measureFrame)measureFrame=requestAnimationFrame(measureCards);};
    const prepareNeighbours=()=>{
      [-1,0,1].forEach(delta=>{
        const card=cards[(index+delta+cards.length)%cards.length];
        card.querySelectorAll('.card__media img').forEach(image=>{
          image.loading='eager';image.decoding='async';
          if(image.complete&&image.naturalWidth&&image.decode)image.decode().catch(()=>{});
        });
      });
    };
    const offsetFor=i=>{let offset=(i-index+cards.length)%cards.length;if(offset>cards.length/2)offset-=cards.length;return offset;};
    const paintMobile=(progress=0)=>{
      cards.forEach((card,i)=>{
        const offset=offsetFor(i)+progress, distance=Math.min(2,Math.abs(offset));
        card.style.setProperty('--aura-rec-position',offset);
        card.style.setProperty('--aura-rec-scale',1-.12*Math.min(1,distance));
        card.style.setProperty('--aura-rec-opacity',Math.max(0,1-.5*distance));
        card.style.setProperty('--aura-rec-layer',String(10-Math.round(distance*3)));
        card.style.visibility='visible';
      });
    };
    const render = () => {
      cards.forEach((card,i) => {
        const offset=offsetFor(i), selected=offset===0;
        card.classList.toggle('is-selected',selected);card.classList.toggle('is-neighbour',Math.abs(offset)===1);card.inert=!selected;
        card.setAttribute('aria-hidden',String(!selected));
        card.style.setProperty('--aura-rec-offset',Math.max(-2,Math.min(2,offset)));
        card.style.visibility=mobileCards.matches||Math.abs(offset)<=1?'visible':'hidden';
      });
      if(mobileCards.matches)paintMobile();
      prepareNeighbours();
      if(!mobileCards.matches)queueMeasure();
      root.querySelector('[data-aura-recommendation-status]').textContent=`${index+1} of ${cards.length}`;
    };
    const move=delta=>{index=(index+delta+cards.length)%cards.length;render();};
    root.querySelector('[data-aura-rec-prev]').addEventListener('click',()=>move(-1));
    root.querySelector('[data-aura-rec-next]').addEventListener('click',()=>move(1));
    let dragFrame=0,dragX=0,suppressClick=0;
    const resetDrag=()=>{cancelAnimationFrame(dragFrame);dragFrame=0;list.classList.remove('is-dragging');list.style.setProperty('--aura-rec-drag','0px');};
    list.addEventListener('pointerdown',e=>{
      if(e.button!==0||e.isPrimary===false||e.target.closest('button,select,input')||(!mobileCards.matches&&e.target.closest('a')))return;
      start={x:e.clientX,y:e.clientY,id:e.pointerId,time:performance.now(),dragging:false};
    });
    list.addEventListener('pointermove',e=>{
      if(!start||e.pointerId!==start.id||!mobileCards.matches)return;
      const dx=e.clientX-start.x,dy=e.clientY-start.y;
      if(!start.dragging){
        if(Math.abs(dy)>8&&Math.abs(dy)>=Math.abs(dx)){start=null;return;}
        if(Math.abs(dx)<8||Math.abs(dx)<Math.abs(dy)*1.2)return;
        start.dragging=true;list.setPointerCapture(e.pointerId);list.classList.add('is-dragging');
      }
      dragX=Math.max(-1,Math.min(1,dx/swipeStep));
      if(!dragFrame)dragFrame=requestAnimationFrame(()=>{dragFrame=0;paintMobile(dragX);});
    },{passive:true});
    list.addEventListener('pointerup',e=>{
      if(!start||e.pointerId!==start.id)return;
      const dx=e.clientX-start.x,dy=e.clientY-start.y,dragged=start.dragging,elapsed=Math.max(1,performance.now()-start.time);
      start=null;
      if(dragged)suppressClick=performance.now()+450;
      resetDrag();
      const commit=Math.abs(dx)>Math.abs(dy)&&(Math.abs(dx)>Math.max(35,swipeStep*.2)||(dragged&&Math.abs(dx)>16&&Math.abs(dx)/elapsed>.45));
      if(commit)move(dx<0?1:-1);else render();
      if(list.hasPointerCapture(e.pointerId))list.releasePointerCapture(e.pointerId);
    });
    list.addEventListener('pointercancel',()=>{start=null;resetDrag();render();});
    list.addEventListener('dragstart',e=>e.preventDefault());
    list.addEventListener('click',e=>{if(performance.now()<suppressClick){e.preventDefault();e.stopPropagation();}},true);
    mobileCards.addEventListener('change',()=>{start=null;resetDrag();render();queueMeasure();});
    const observer=new ResizeObserver(()=>{if(!root.isConnected){observer.disconnect();cancelAnimationFrame(measureFrame);return;}queueMeasure();});cards.forEach(card=>observer.observe(card));
    render();
    queueMeasure();
  }
  function boot() {
    document.querySelectorAll('product-recommendations').forEach(root=>{
      initRecommendations(root);
      if(root.dataset.auraRecommendationsObserved)return;
      root.dataset.auraRecommendationsObserved='true';
      new MutationObserver(()=>initRecommendations(root)).observe(root,{childList:true});
    });
  }
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',boot,{once:true});else boot();
  document.addEventListener('shopify:section:load',boot);
  document.addEventListener('toggle',e=>{
    const panel=e.target;
    if(!panel.matches?.('menu-drawer details,.mobile-facets__disclosure')||!panel.open||!motion())return;
    const links=[...panel.querySelectorAll('.menu-drawer__menu-item,.header__submenu .header__menu-item,.mobile-facets__list > li')].filter(el=>el.getBoundingClientRect().height>0);
    links.slice(0,12).forEach((el,i)=>window.AuraInteractions.animate(el,[{opacity:.25,transform:'translateX(-14px)'},{opacity:1,transform:'translateX(0)'}],{duration:360,delay:i*28,easing:'ease-out',fill:'backwards'}));
  },true);
})();

/* Decorate the existing variant controls without replacing their selection logic. */
(() => {
  const roots = new WeakSet();
  function boot() {
    document.querySelectorAll('product-info[id^="MainProduct-"]').forEach(root => {
      if (roots.has(root)) return;
      roots.add(root);
      let frame = 0;
      const paint = () => {
        frame = 0;
        const data = root.querySelector('[data-aura-gallery-map]');
        if (!data) return;
        const map = JSON.parse(data.textContent);
        const decorate = (input, label) => {
          if (!label) return;
          const variant = map.variants.find(v => v.options[map.colourIndex] === input.value && v.image);
          if (!variant) return;
          let image = label.querySelector('.aura-variant-preview');
          if (!image) {
            image = document.createElement('img');
            image.className = 'aura-variant-preview'; image.alt = ''; image.width = 52; image.height = 52;
            image.loading = 'eager'; image.decoding = 'async'; image.fetchPriority = 'low'; label.append(image); label.classList.add('aura-image-swatch');
          }
          const thumb = new URL(variant.image, location.href); if(thumb.pathname.includes('/cdn/shop/') || thumb.hostname.endsWith('shopify.com')) thumb.searchParams.set('width','104');
          if (image.getAttribute('src') !== thumb.href) image.src = thumb.href;
          let name = label.querySelector('.aura-swatch-name');
          if (!name) { name = document.createElement('span'); name.className = 'aura-swatch-name'; name.setAttribute('aria-hidden','true'); label.append(name); }
          if (name.textContent !== input.value) name.textContent = input.value;
        };
        root.querySelectorAll('.g-variant-color-detail input[type="radio"]').forEach(input => decorate(input, input.nextElementSibling));
        root.querySelectorAll('variant-selects fieldset').forEach(group => {
          if (/colou?r/i.test(group.querySelector('legend')?.textContent || '')) {
            group.querySelectorAll('input[type="radio"]').forEach(input => decorate(input, input.nextElementSibling));
          } else if (/size/i.test(group.querySelector('legend')?.textContent || '')) group.classList.add('aura-size-options');
        });
      };
      const queue = () => { if (!frame) frame = requestAnimationFrame(paint); };
      new MutationObserver(queue).observe(root, {childList:true,subtree:true});
      root.addEventListener('change', queue);
      paint();
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',boot,{once:true}); else boot();
  document.addEventListener('shopify:section:load',boot);
})();

/* Compact main-product colours without changing native variant controls. */
(() => {
  const groups = new WeakMap();
  function refresh(root) {
    root.querySelectorAll('.g-variant-color-detail,variant-selects fieldset').forEach(group => {
      if (!group.matches('.g-variant-color-detail') && !/colou?r/i.test(group.querySelector('legend')?.textContent || '')) return;
      const inputs = [...group.querySelectorAll('input[type="radio"]')];
      if (inputs.length <= 5) return;
      let state = groups.get(group);
      if (!state) {
        const button = document.createElement('button');
        button.type = 'button'; button.className = 'aura-more-colours';
        state = {expanded:false,button}; groups.set(group,state);
        button.addEventListener('click', () => {state.expanded=!state.expanded;refresh(root);});
        group.after(button);
      }
      const ranked = inputs.map((input,index) => ({input,index,score:
        (input.checked ? 100 : 0) +
        (!input.disabled && !input.classList.contains('disabled') && !input.closest('li')?.classList.contains('sold-out') ? 10 : 0) +
        (input.nextElementSibling?.querySelector('.aura-variant-preview') ? 1 : 0)
      })).sort((a,b)=>b.score-a.score||a.index-b.index);
      const featured = new Set(ranked.slice(0,5).map(item=>item.input));
      inputs.forEach(input => {
        const hidden = !state.expanded && !featured.has(input);
        const item = input.closest('li') || input.nextElementSibling;
        item?.classList.toggle('aura-colour-overflow',hidden);
      });
      const text = state.expanded ? 'Show fewer colours' : '+'+(inputs.length-5)+' more colours';
      if (state.button.textContent !== text) state.button.textContent=text;
      state.button.setAttribute('aria-expanded',String(state.expanded));
    });
  }
  const roots=new WeakSet();
  function boot() {
    document.querySelectorAll('product-info[id^="MainProduct-"]').forEach(root=>{
      if(roots.has(root))return;roots.add(root);
      let frame=0;
      const queue=()=>{if(!frame)frame=requestAnimationFrame(()=>{frame=0;refresh(root);});};
      new MutationObserver(queue).observe(root,{childList:true,subtree:true});
      root.addEventListener('change',queue);root.addEventListener('click',queue);
      refresh(root);
    });
  }
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',boot,{once:true});else boot();
  document.addEventListener('shopify:section:load',boot);
})();



/* Compact, colour-aware product image controls. */
(() => {
  function boot() {
    document.querySelectorAll('product-info[id^="MainProduct-"] media-gallery').forEach(gallery => {
      if (gallery.dataset.auraViewsReady) return;
      let viewer = gallery.querySelector('[id^="GalleryViewer"]');
      if (!viewer || typeof gallery.setActiveMedia !== 'function') return;
      gallery.dataset.auraViewsReady = 'true';
      const controls = document.createElement('div');
      controls.className = 'aura-image-views';
      controls.setAttribute('role', 'group');
      controls.setAttribute('aria-label', 'Product image views');
      gallery.append(controls);
      let items = [], signature = '', frame = 0;
      const sync = () => {
        frame = 0;
        viewer = gallery.querySelector('[id^="GalleryViewer"]');
        if (!viewer) return;
        items = [...viewer.querySelectorAll('li[data-media-id]')].filter(item => item.dataset.auraColourVisible !== 'false');
        const current = gallery.querySelector('[data-target] button[aria-current="true"]')?.closest('[data-target]')?.dataset.target;
        const active = items.find(item => item.dataset.mediaId === current) || items.find(item => item.classList.contains('is-active')) || items[0];
        const labels = items.map(item => {
          const image = item.querySelector('img');
          const text = (image?.alt || '') + ' ' + (image?.getAttribute('src') || '');
          return /(?:^|[\s_\-/])front(?:[\s_\-.]|$)/i.test(text) ? 'Front' : /(?:^|[\s_\-/])back(?:[\s_\-.]|$)/i.test(text) ? 'Back' : '';
        });
        if (items.length === 2 && labels.filter(Boolean).length === 1) {
          const known = labels.find(Boolean);
          labels[labels.indexOf('')] = known === 'Front' ? 'Back' : 'Front';
        }
        // Keep repeated named views distinguishable without guessing an image's side.
        const viewCounts = labels.reduce((counts, label) => { if (label) counts[label] = (counts[label] || 0) + 1; return counts; }, {});
        const viewSeen = {};
        labels.forEach((label, i) => {
          if (label && viewCounts[label] > 1) labels[i] = `${label} ${viewSeen[label] = (viewSeen[label] || 0) + 1}`;
        });
        const next = items.map((item,i) => item.dataset.mediaId + ':' + labels[i]).join('|');
        if (next !== signature) {
          signature = next;
          controls.replaceChildren();
          items.forEach((item,i) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.textContent = labels[i] || `View ${i+1}`;
            button.setAttribute('aria-controls', item.id);
            button.addEventListener('click', () => { if (typeof gallery.selectMedia === 'function') gallery.selectMedia(item.dataset.mediaId); else gallery.setActiveMedia(item.dataset.mediaId, false); sync(); });
            controls.append(button);
          });
        }
        controls.hidden = items.length < 2;
        [...controls.children].forEach((button,i) => button.setAttribute('aria-pressed', String(items[i] === active)));
      };
      const schedule = () => { if (!frame) frame = requestAnimationFrame(sync); };
      new MutationObserver(schedule).observe(gallery, {subtree:true, childList:true, attributes:true, attributeFilter:['class','data-aura-colour-visible','aria-current']});
      sync();
    });
  }
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',boot,{once:true});else boot();
  customElements.whenDefined('media-gallery').then(boot);
  document.addEventListener('product-info:loaded', () => requestAnimationFrame(boot));
  document.addEventListener('shopify:section:load',boot);
})();

/* Mobile Curate sheet dismissal: capture only the handle/header, never filter scrolling. */
(() => {
 const boot=()=>document.querySelectorAll('.aura-curation__panel').forEach(panel=>{
  if(panel.querySelector('.aura-curation__pull'))return;
  const head=panel.querySelector('.aura-curation__head'),close=panel.querySelector('[data-curate-close]');if(!head||!close)return;
  const pull=document.createElement('button');pull.type='button';pull.className='aura-curation__pull';pull.textContent='Swipe down to close';pull.setAttribute('aria-label','Close filters');head.before(pull);
  let suppressClick=false;
  pull.onclick=e=>{if(suppressClick){e.preventDefault();return;}close.click();};
  [pull,head].forEach(surface=>{
   let start=null,dy=0;
   surface.style.touchAction='none';
   surface.style.userSelect='none';
   surface.addEventListener('pointerdown',e=>{
    if(!matchMedia('(max-width:749px)').matches||e.button>0||e.target.closest('[data-curate-close]'))return;
    start={x:e.clientX,y:e.clientY,id:e.pointerId};dy=0;suppressClick=false;surface.setPointerCapture(e.pointerId);
   });
   surface.addEventListener('pointermove',e=>{
    if(!start||e.pointerId!==start.id)return;
    dy=Math.max(0,e.clientY-start.y);
    if(dy>5){suppressClick=true;panel.style.transform='translateY('+dy+'px)';}
   });
   const finish=e=>{
    if(!start)return;
    const dismiss=e.type!=='pointercancel'&&dy>60&&dy>Math.abs(e.clientX-start.x)*1.2;
    start=null;
    if(dismiss){close.click();}
    else{
     const from=panel.style.transform||'none';panel.style.transform='';
     if(dy>5&&!matchMedia('(prefers-reduced-motion: reduce)').matches)panel.animate([{transform:from},{transform:'none'}],{duration:220,easing:'ease-out'});
    }
    /* Reset click suppression on the next pointerdown, after any synthetic click. */
   };
   surface.addEventListener('pointerup',finish);surface.addEventListener('pointercancel',finish);
  });
  panel.addEventListener('close',()=>{panel.style.transform='';});
 });
 if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',boot,{once:true});else boot();
 document.addEventListener('shopify:section:load',boot);
})();

/* Match banner frames to uploaded artwork proportions. */
(() => {
 const boot=()=>document.querySelectorAll('.aura-orbit__card img').forEach(img=>{
  const fit=()=>{if(!img.naturalWidth||!img.naturalHeight)return;const hero=img.closest('.banner__media');const frame=hero||img.closest('.aura-orbit__card');frame?.style.setProperty(hero?'--aura-hero-ratio':'--aura-art-ratio',String(img.naturalWidth/img.naturalHeight));};
  if(img.complete)fit();else img.addEventListener('load',fit,{once:true});
 });
 if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',boot,{once:true});else boot();
 document.addEventListener('shopify:section:load',boot);
})();

/* Use the displayed variant image proportions, including app-swapped images. */
(() => {
 const fit=img=>{if(!img.matches?.('product-info .product__media img')||!img.naturalWidth||!img.naturalHeight)return;img.closest('.product__media').style.setProperty('--aura-image-ratio',String(img.naturalWidth/img.naturalHeight));};
 document.querySelectorAll('product-info .product__media img').forEach(fit);
 document.addEventListener('load',e=>fit(e.target),true);
})();

/* Aura shopping refinement: use the existing product form and app size chart. */
(() => {
  const product = document.querySelector('product-info[id^="MainProduct-"]');
  if (!product) return;
  const mobile = matchMedia('(max-width: 749px)');
  const bar = document.createElement('aside');
  bar.className = 'aura-purchase-bar';
  bar.setAttribute('aria-label', 'Your Aura selection');
  bar.hidden = true;
  bar.innerHTML = '<div><strong>Your Aura Awaits</strong><span data-aura-purchase-selection></span></div><button type="button" class="button" data-aura-purchase-add>Add to Cart</button>';
  document.body.append(bar);
  const action = bar.querySelector('button'), selection = bar.querySelector('[data-aura-purchase-selection]');
  bar.querySelector('strong').textContent = product.querySelector('.product__title h1, .product__title h2')?.textContent.trim() || 'Your Aura Awaits';
  const original = () => product.querySelector('product-form button.product-form__submit[name="add"]');
  let observedButton, passed = false, frame = 0;
  const visibility = () => { bar.hidden = !passed; };
  // App-hidden ancestors can keep IntersectionObserver at a zero ratio during scrolling.
  let visibilityFrame = 0;
  const refreshVisibility = () => {
    if (visibilityFrame) return;
    visibilityFrame = requestAnimationFrame(() => {
      visibilityFrame = 0;
      const button = original();
      passed = !!button && button.getBoundingClientRect().bottom < 0;
      visibility();
    });
  };
  addEventListener('scroll', refreshVisibility, { passive: true });
  mobile.addEventListener('change', refreshVisibility);
  const intersection = new IntersectionObserver(entries => {
    const entry = entries[entries.length - 1];
    passed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
    visibility();
  });
  function sync() {
    frame = 0;
    if (!product.isConnected) { intersection.disconnect(); changes.disconnect(); bar.remove(); return; }
    const button = original();
    if (!button) { bar.hidden = true; return; }
    if (observedButton !== button) { intersection.disconnect(); intersection.observe(button); observedButton = button; }
    const busy = button.disabled || button.getAttribute('aria-disabled') === 'true';
    action.disabled = busy;
    action.textContent = button.querySelector('span')?.textContent.trim() || 'Add to Cart';
    const picker = product.querySelector('.globo-swatch-list') || product.querySelector('variant-selects');
    const options = picker ? [...picker.querySelectorAll('input:checked, select option:checked')].map(e => e.value).filter(Boolean) : [];
    const price = product.querySelector('.price:not(.hidden)');
    const amount = price?.querySelector(price.classList.contains('price--on-sale') ? '.price-item--sale' : '.price-item--regular')?.textContent.trim();
    selection.textContent = [...options, amount].filter(Boolean).join(' · ');
    visibility();
    sizeGuide();
    refreshVisibility();
  }
  const queue = () => { if (!frame) frame = requestAnimationFrame(sync); };
  const changes = new MutationObserver(queue);
  changes.observe(product, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });
  product.addEventListener('change', queue);
  mobile.addEventListener('change', visibility);
  action.addEventListener('click', () => {
    const button = original();
    if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;
    // Move back to the real button so its existing feedback and any errors are visible.
    button.scrollIntoView({ block: 'center', behavior: 'instant' });
    button.focus({ preventScroll: true });
    button.click();
  });
  sync();

  function sizeGuide() {
    const chart = document.querySelector('.float-wrapper[role="button"][aria-label="Size chart"]');
    const size = [...product.querySelectorAll('.swatch--gl'), ...product.querySelectorAll('variant-selects fieldset, variant-selects .product-form__input--dropdown')].find(e => /\bsize\b/i.test(e.querySelector('legend,label.form__label')?.textContent || ''));
    if (!chart || !size) return false;
    // Keep the app trigger for the inline guide, without a duplicate floating tab.
    chart.style.setProperty('display', 'none', 'important');
    // Move existing controls so app bindings, variant IDs and keyboard order survive.
    const sizeRank = value => {
      const key = String(value || '').trim().toUpperCase().replace(/\s+/g, '');
      const ranks = {XXS:0, XS:1, S:2, M:3, L:4, XL:5, XXL:6, '2XL':6, XXXL:7, '3XL':7, '4XL':8, '5XL':9};
      return ranks[key] ?? 100;
    };
    const sizeList = size.querySelector('ul.value');
    if (sizeList) {
      const controls = [...sizeList.children];
      const ordered = [...controls].sort((a,b) => sizeRank(a.dataset.value) - sizeRank(b.dataset.value));
      if (ordered.some((node,index) => node !== controls[index])) ordered.forEach(node => sizeList.append(node));
    }
    const existing = product.querySelector('[data-aura-size-guide]');
    if (existing && existing.parentElement !== size) size.append(existing);
    if (!existing) {
      const link = document.createElement('button');
      link.type = 'button'; link.dataset.auraSizeGuide = ''; link.className = 'aura-size-guide';
      link.innerHTML = '<svg aria-hidden="true" viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="7" width="18" height="10" rx="2"/><path d="M7 7v4m5-4v6m5-6v4"/></svg><span>Size guide</span>';
      if (!document.getElementById('aura-size-button-style')) {
        const style = document.createElement('style');
        style.id = 'aura-size-button-style';
        style.textContent = '.swatch--gl:has(> .aura-size-guide){display:grid!important;grid-template-columns:minmax(0,1fr) auto;align-items:center;gap:10px 12px}.swatch--gl:has(> .aura-size-guide)::before,.swatch--gl:has(> .aura-size-guide)::after{display:none!important}.swatch--gl:has(> .aura-size-guide)>legend{position:absolute!important;width:1px!important;height:1px!important;padding:0!important;margin:-1px!important;overflow:hidden!important;clip-path:inset(50%)!important;white-space:nowrap!important;border:0!important}.swatch--gl:has(> .aura-size-guide)>ul.value{grid-column:1/-1;grid-row:2;width:100%;margin:0!important}.swatch--gl>.aura-size-guide{grid-column:2;grid-row:1;display:inline-flex!important;align-items:center;justify-content:center;gap:7px;min-height:44px;margin:0!important;padding:8px 13px!important;border:1px solid #817650!important;border-radius:999px!important;background:transparent!important;color:#cebd87!important;font-size:13px!important;line-height:1.2!important;text-decoration:none!important;white-space:nowrap;transition:background .18s ease,border-color .18s ease,transform .18s ease}.swatch--gl>.aura-size-guide:focus-visible{outline:2px solid #cebd87;outline-offset:3px}.swatch--gl>.aura-size-guide:active{transform:scale(.97)}@media(hover:hover){.swatch--gl>.aura-size-guide:hover{background:rgba(206,189,135,.1)!important;border-color:#cebd87!important;transform:translateY(-1px)}}@media(prefers-reduced-motion:reduce){.swatch--gl>.aura-size-guide{transition:none;transform:none!important}}.swatch--gl:has(> .aura-size-guide){display:flex!important;flex-wrap:wrap;align-items:center;gap:6px!important}.swatch--gl:has(> .aura-size-guide)>ul.value{display:contents!important}.swatch--gl:has(> .aura-size-guide)>ul.value>li{margin:0!important;float:none!important;list-style:none}.swatch--gl>.aura-size-guide{flex:0 0 auto}@media(max-width:749px){.swatch--gl:has(> .aura-size-guide)>ul.value>li label{box-sizing:border-box;min-width:44px!important;min-height:44px!important;padding:0 6px!important;display:flex!important;align-items:center;justify-content:center}.swatch--gl>.aura-size-guide{width:44px;min-width:44px;padding:0!important}.swatch--gl>.aura-size-guide>span{position:absolute;width:1px;height:1px;overflow:hidden;clip-path:inset(50%);white-space:nowrap}.swatch--gl>.aura-size-guide>svg{width:20px;height:20px}.swatch--gl:has(> .aura-size-guide) .attr-title-hover{display:none!important}}@media(hover:none),(pointer:coarse){.swatch--gl:has(> .aura-size-guide) .attr-title-hover{display:none!important}}';
        document.head.append(style);
      }
      link.addEventListener('click', () => document.querySelector('.float-wrapper[role="button"][aria-label="Size chart"]')?.click());
      size.append(link);
    }
    return true;
  }
  let reviewObserved;
  function reviews() {
    const widget = document.getElementById('judgeme_product_reviews');
    if (!widget) return false;
    function polish() {
      widget.querySelectorAll('.jdgm-empty-state p').forEach(p => {
        if (p.textContent.trim() !== 'No items found') return;
        const hasInvitation = /Be the first to share/i.test(widget.textContent);
        if (hasInvitation) p.closest('.jdgm-empty-state').hidden = true;
        else p.textContent = 'Your Aura. Your Story. Be the first to share your experience.';
      });
    }
    if (reviewObserved !== widget) {
      reviewObserved = widget;
      new MutationObserver(polish).observe(widget, { childList: true, subtree: true, characterData: true });
    }
    polish(); return true;
  }
  let discoveryFrame = 0;
  const discover = () => {
    discoveryFrame = 0;
    const guideReady = sizeGuide(), reviewsReady = reviews();
    if (guideReady && reviewsReady) discovery.disconnect();
  };
  const discovery = new MutationObserver(() => { if (!discoveryFrame) discoveryFrame = requestAnimationFrame(discover); });
  discovery.observe(document.body, { childList: true, subtree: true });
  discover();
  // Variant section replacement may replace the size controls without reloading the page.
  product.addEventListener('change', () => requestAnimationFrame(sizeGuide));
  setTimeout(() => discovery.disconnect(), 20000);
})();
